import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import ProductWordmark from './ProductWordmark';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import VerifyCodeForm from './VerifyCodeForm';
import OnboardingForm from './OnboardingForm';
import AcceptInvitationForm from './AcceptInvitationForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import ResetPasswordForm from './ResetPasswordForm';
import { isPasswordValid } from './password';
import { PHONE_NUMBER_ERROR, isPhoneNumberValid } from '../inputs/phoneNumber';

export default function AuthShell({
    forceOnboarding = false,
    profileLoading = false,
    onAuthenticated,
    onOnboardingComplete
}) {
    const location = useLocation();
    const navigate = useNavigate();

    const [mode, setMode] = useState(forceOnboarding ? 'onboarding' : 'login');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [resetPassword, setResetPassword] = useState('');
    const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');

    const [signup, setSignup] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        company: '',
        terms: false
    });

    const [otpEmail, setOtpEmail] = useState('');
    const [otpPurpose, setOtpPurpose] = useState('login');
    const [otpChallenge, setOtpChallenge] = useState('');
    const [digits, setDigits] = useState(['', '', '', '', '', '']);
    const [rememberDevice, setRememberDevice] = useState(false);
    const [resendBusy, setResendBusy] = useState(false);

    const [onboardingStep, setOnboardingStep] = useState('role');
    const [jobRole, setJobRole] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [usagePurpose, setUsagePurpose] = useState('');

    const [googleClientId, setGoogleClientId] = useState('');
    const [googleSdkLoaded, setGoogleSdkLoaded] = useState(!!window.google);

    const [resetToken, setResetToken] = useState('');
    const [resetVerifyLoading, setResetVerifyLoading] = useState(false);
    const [resetVerifyError, setResetVerifyError] = useState('');
    const [inviteToken, setInviteToken] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteDetails, setInviteDetails] = useState(null);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteVerifyError, setInviteVerifyError] = useState('');
    const [inviteForm, setInviteForm] = useState({
        existingPassword: '',
        name: '',
        title: '',
        mobile: '',
        password: ''
    });

    useEffect(() => {
        if (forceOnboarding) {
            setMode('onboarding');
        }
    }, [forceOnboarding]);

    useEffect(() => {
        if (mode !== 'onboarding' && mode !== 'accept-invitation') return;
        const loadGeo = async () => {
            try {
                const res = await fetch('/api/auth/geo');
                if (res.ok) {
                    const data = await res.json();
                    if (data.countryCode) setCountryCode(data.countryCode);
                }
            } catch {
                // Keep the country field empty if lookup fails.
            }
        };
        loadGeo();
    }, [mode]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const resetTokenParam = queryParams.get('resetToken');
        const inviteTokenParam = queryParams.get('inviteToken');
        const emailParam = queryParams.get('email');

        if (resetTokenParam && emailParam) {
            setResetToken(resetTokenParam);
            setEmail(emailParam);
            setOtpEmail(emailParam);
            setMode('reset-password');
            setResetVerifyLoading(true);
            setResetVerifyError('');
            setError('');
            setSuccess('');

            const verifyReset = async () => {
                try {
                    const res = await fetch('/api/auth/reset-password/verify', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ token: resetTokenParam, email: emailParam })
                    });
                    if (!res.ok) {
                        const errData = await res.json();
                        setResetVerifyError(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'This reset link is invalid or has expired.');
                    }
                } catch {
                    setResetVerifyError('Failed to verify reset link with Identity Provider.');
                } finally {
                    setResetVerifyLoading(false);
                }
            };
            verifyReset();
            navigate(location.pathname, { replace: true });
            return;
        }

        if (!inviteTokenParam || !emailParam) return;

        setInviteToken(inviteTokenParam);
        setInviteEmail(emailParam);
        setMode('accept-invitation');
        setInviteLoading(true);
        setInviteVerifyError('');

        const verifyInvitation = async () => {
            try {
                const res = await fetch(`/api/auth/invitation/verify?token=${inviteTokenParam}&email=${encodeURIComponent(emailParam)}`);
                if (res.ok) {
                    setInviteDetails(await res.json());
                } else {
                    const errData = await res.json();
                    setInviteVerifyError(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Invitation link is invalid or expired.');
                }
            } catch {
                setInviteVerifyError('Failed to verify invitation with Identity Provider.');
            } finally {
                setInviteLoading(false);
            }
        };
        verifyInvitation();
        navigate(location.pathname, { replace: true });
    }, [location, navigate]);

    useEffect(() => {
        if (forceOnboarding) return;
        const fetchGoogleClientId = async () => {
            try {
                const res = await fetch('/api/auth/google-client-id');
                if (!res.ok) return;
                const data = await res.json();
                if (!data.clientId) return;
                setGoogleClientId(data.clientId);
                if (window.google) {
                    setGoogleSdkLoaded(true);
                    return;
                }
                const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
                if (existingScript) {
                    existingScript.addEventListener('load', () => setGoogleSdkLoaded(true));
                    return;
                }
                const script = document.createElement('script');
                script.src = 'https://accounts.google.com/gsi/client';
                script.async = true;
                script.defer = true;
                script.onload = () => setGoogleSdkLoaded(true);
                document.body.appendChild(script);
            } catch (err) {
                console.error('Could not retrieve Google Client ID', err);
            }
        };
        fetchGoogleClientId();
    }, [forceOnboarding]);

    useEffect(() => {
        if (!googleClientId || !googleSdkLoaded || !window.google || mode !== 'login') return;
        window.google.accounts.id.initialize({
            client_id: googleClientId,
            callback: handleGoogleCredentialResponse,
            context: 'signin',
            ux_mode: 'popup',
            auto_select: false
        });
        const googleButton = document.getElementById('google-signin-btn');
        const width = Math.round(googleButton?.getBoundingClientRect().width || 0);
        const buttonOptions = { theme: 'outline', size: 'large', logo_alignment: 'left' };
        if (width > 0) buttonOptions.width = width;
        window.google.accounts.id.renderButton(googleButton, buttonOptions);
    }, [googleClientId, googleSdkLoaded, mode]);

    const completeAuth = (data) => {
        if (data.rememberDeviceToken && (otpEmail || email)) {
            localStorage.setItem(`remember_device_token_${(otpEmail || email).toLowerCase()}`, data.rememberDeviceToken);
        }
        sessionStorage.setItem('token', data.token);
        const waitingOnAccess = data.accessRequest?.status === 'Pending' || data.accessRequest?.status === 'Rejected';
        onAuthenticated({
            token: data.token,
            needsOnboarding: waitingOnAccess ? false : !!data.needsOnboarding
        });
        if (data.needsOnboarding && !waitingOnAccess) {
            setMode('onboarding');
            setOnboardingStep('role');
            setError('');
        }
    };

    const handleGoogleCredentialResponse = async (response) => {
        setError('');
        try {
            const res = await fetch('/api/auth/google-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: response.credential })
            });
            if (res.ok) {
                completeAuth(await res.json());
            } else {
                const errorData = await res.json();
                setError(Array.isArray(errorData) ? errorData.map(e => e.description).join(' ') : 'Google login failed.');
            }
        } catch {
            setError('Could not connect to Identity Provider (Port 5213).');
        }
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');
        try {
            const rememberDeviceToken = localStorage.getItem(`remember_device_token_${email.toLowerCase()}`);
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    password,
                    rememberDeviceToken: rememberDeviceToken || null
                })
            });
            if (res.ok) {
                const data = await res.json();
                if (data.requiresTwoFactor) {
                    setOtpEmail(data.email);
                    setOtpPurpose('login');
                    setOtpChallenge(data.otpChallenge || '');
                    setDigits(['', '', '', '', '', '']);
                    setMode('verify');
                    if (data.devCode) {
                        setSuccess(`Developer code: ${data.devCode}`);
                    }
                } else {
                    completeAuth(data);
                }
            } else {
                setError('Login failed. Please verify credentials.');
            }
        } catch {
            setError('Could not connect to Identity Provider (Port 5213).');
        }
    };

    const handleSignup = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');
        if (!signup.terms) {
            setError('You must agree to the Terms of Service to continue.');
            return;
        }
        if (signup.password !== signup.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        try {
            const res = await fetch('/api/auth/signup-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName: signup.firstName,
                    lastName: signup.lastName,
                    email: signup.email,
                    password: signup.password,
                    company: signup.company,
                    termsAccepted: signup.terms
                })
            });
            if (res.ok) {
                const data = await res.json();
                setOtpEmail(data.email || signup.email);
                setOtpPurpose('signup');
                setOtpChallenge(data.otpChallenge || '');
                setDigits(['', '', '', '', '', '']);
                setMode('verify');
                if (data.devCode) {
                    setSuccess(`Developer code: ${data.devCode}`);
                }
            } else {
                const errorData = await res.json();
                setError(Array.isArray(errorData) ? errorData.map(e => e.description).join(' ') : 'Signup failed.');
            }
        } catch {
            setError('Could not connect to Identity Provider (Port 5213).');
        }
    };

    const handleVerify = async (event) => {
        event.preventDefault();
        setError('');
        const code = digits.join('');
        try {
            const res = await fetch('/api/auth/verify-email-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: otpEmail,
                    code,
                    purpose: otpPurpose,
                    challengeToken: otpChallenge,
                    rememberDevice
                })
            });
            if (res.ok) {
                const data = await res.json();
                const waiting = otpPurpose === 'signup'
                    && (data.accessRequest?.status === 'Pending' || data.accessRequest?.status === 'Rejected');
                if (waiting) {
                    sessionStorage.removeItem('token');
                    setMode('access-received');
                    setError('');
                    setSuccess('');
                    return;
                }
                completeAuth(data);
            } else {
                const errorData = await res.json();
                setError(Array.isArray(errorData) ? errorData.map(e => e.description).join(' ') : 'Invalid verification code.');
            }
        } catch {
            setError('Could not connect to Identity Provider (Port 5213).');
        }
    };

    const handleForgotPassword = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');
        try {
            const res = await fetch('/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, frontendOrigin: window.location.origin })
            });
            if (res.ok) {
                const data = await res.json();
                setSuccess(data.resetUrl
                    ? (
                        <div>
                            <p>Check your email for a password reset link.</p>
                            <p className="auth-support">
                                <a href={data.resetUrl}>Developer reset link</a>
                            </p>
                        </div>
                    )
                    : (data.message || 'If an account exists for that email, a reset link has been sent.')
                );
            } else {
                const errorData = await res.json();
                setError(Array.isArray(errorData) ? errorData.map(e => e.description).join(' ') : 'Could not send reset link.');
            }
        } catch {
            setError('Could not connect to Identity Provider (Port 5213).');
        }
    };

    const handleResetPassword = async (event) => {
        event.preventDefault();
        setError('');
        if (!isPasswordValid(resetPassword) || resetPassword !== resetPasswordConfirm) {
            setError('Password does not meet complexity requirements.');
            return;
        }
        try {
            const res = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: otpEmail || email,
                    token: resetToken,
                    newPassword: resetPassword
                })
            });
            if (res.ok) {
                const data = await res.json();
                setPassword('');
                setResetPassword('');
                setResetPasswordConfirm('');
                setResetToken('');
                setMode('login');
                setSuccess(data.message || 'Password updated. You can now sign in with your new password.');
            } else {
                const errorData = await res.json();
                setError(Array.isArray(errorData) ? errorData.map(e => e.description).join(' ') : 'Could not reset password.');
            }
        } catch {
            setError('Could not connect to Identity Provider (Port 5213).');
        }
    };

    const handleResend = async () => {
        setError('');
        setResendBusy(true);
        try {
            const res = await fetch('/api/auth/resend-email-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: otpEmail, purpose: otpPurpose, challengeToken: otpChallenge })
            });
            if (res.ok) {
                const data = await res.json();
                setSuccess(data.devCode ? `Developer code: ${data.devCode}` : 'A new code has been sent.');
            } else {
                const errorData = await res.json();
                setError(Array.isArray(errorData) ? errorData.map(e => e.description).join(' ') : 'Could not resend code.');
            }
        } catch {
            setError('Could not connect to Identity Provider (Port 5213).');
        } finally {
            setResendBusy(false);
        }
    };

    const handleOnboardingContinue = async () => {
        setError('');
        if (onboardingStep === 'role') {
            setOnboardingStep('country');
            return;
        }
        if (onboardingStep === 'country') {
            setOnboardingStep('purpose');
            return;
        }

        try {
            const res = await apiFetch('/api/auth/onboarding', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    jobRole,
                    countryCode,
                    usagePurpose
                })
            });
            if (res.ok) {
                const data = await res.json();
                if (data.token) {
                    sessionStorage.setItem('token', data.token);
                }
                onOnboardingComplete();
            } else {
                const errorData = await res.json();
                setError(Array.isArray(errorData) ? errorData.map(e => e.description).join(' ') : 'Could not save onboarding.');
            }
        } catch {
            setError('Could not save onboarding.');
        }
    };

    const handleAcceptExisting = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');
        try {
            const rememberDeviceToken = localStorage.getItem(`remember_device_token_${inviteEmail.toLowerCase()}`);
            const res = await fetch('/api/auth/invitation/accept', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: inviteToken,
                    email: inviteEmail,
                    password: inviteForm.existingPassword,
                    rememberDeviceToken: rememberDeviceToken || null
                })
            });
            if (res.ok) {
                const data = await res.json();
                if (data.requiresTwoFactor) {
                    setOtpEmail(data.email || inviteEmail);
                    setOtpPurpose('invite-accept');
                    setOtpChallenge(data.otpChallenge || '');
                    setDigits(['', '', '', '', '', '']);
                    setMode('verify');
                    if (data.devCode) {
                        setSuccess(`Developer code: ${data.devCode}`);
                    }
                } else {
                    setOtpEmail(inviteEmail);
                    completeAuth(data);
                }
            } else {
                const errData = await res.json();
                setError(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Failed to accept invitation.');
            }
        } catch {
            setError('Connection error while accepting invitation.');
        }
    };

    const handleInviteRegister = async (event) => {
        event.preventDefault();
        setError('');
        setSuccess('');
        if (!isPhoneNumberValid(inviteForm.mobile, { required: true })) {
            setError(PHONE_NUMBER_ERROR);
            return;
        }
        try {
            const res = await fetch('/api/auth/invitation/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: inviteToken,
                    email: inviteEmail,
                    password: inviteForm.password,
                    name: inviteForm.name,
                    title: inviteForm.title,
                    mobile: inviteForm.mobile
                })
            });
            if (res.ok) {
                const data = await res.json();
                if (data.requiresTwoFactor) {
                    setOtpEmail(data.email || inviteEmail);
                    setOtpPurpose('invite-register');
                    setOtpChallenge(data.otpChallenge || '');
                    setDigits(['', '', '', '', '', '']);
                    setMode('verify');
                    if (data.devCode) {
                        setSuccess(`Developer code: ${data.devCode}`);
                    }
                } else {
                    setOtpEmail(inviteEmail);
                    completeAuth(data);
                }
            } else {
                const errData = await res.json();
                setError(Array.isArray(errData) ? errData.map(e => e.description).join(' ') : 'Failed to register account.');
            }
        } catch {
            setError('Connection error while registering.');
        }
    };

    const resetToLogin = () => {
        setMode('login');
        setError('');
        setSuccess('');
        setDigits(['', '', '', '', '', '']);
        setOtpChallenge('');
    };

    return (
        <div className="auth-shell">
            <div className="auth-panel">
                <ProductWordmark />
                <div className="auth-card">
                    {profileLoading && (
                        <div className="auth-loading">
                            <div className="loader"></div>
                            <p>Loading your workspace...</p>
                        </div>
                    )}

                    {!profileLoading && !forceOnboarding && mode === 'login' && (
                        <LoginForm
                            email={email}
                            password={password}
                            onEmailChange={setEmail}
                            onPasswordChange={setPassword}
                            onSubmit={handleLogin}
                            error={error}
                            success={success}
                            onShowSignup={() => { setMode('signup'); setError(''); setSuccess(''); }}
                            onShowForgotPassword={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                            googleClientId={googleClientId}
                        />
                    )}

                    {!profileLoading && !forceOnboarding && mode === 'signup' && (
                        <SignupForm
                            {...signup}
                            onChange={(field, value) => setSignup(prev => ({ ...prev, [field]: value }))}
                            onSubmit={handleSignup}
                            error={error}
                            onShowLogin={resetToLogin}
                        />
                    )}

                    {!profileLoading && !forceOnboarding && mode === 'access-received' && (
                        <>
                            <h1>Request for access received</h1>
                            <p className="auth-subtitle">
                                A request for access has been received. You will receive an email when, and if, your request is approved.
                            </p>
                            <button type="button" className="btn-primary auth-submit" onClick={resetToLogin}>Return to login</button>
                        </>
                    )}

                    {!profileLoading && !forceOnboarding && mode === 'verify' && (
                        <VerifyCodeForm
                            email={otpEmail}
                            digits={digits}
                            onDigitChange={(index, digit) => {
                                setDigits(prev => {
                                    const next = [...prev];
                                    next[index] = digit;
                                    return next;
                                });
                            }}
                            onPaste={(event) => {
                                const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
                                if (!pasted) return;
                                event.preventDefault();
                                const next = ['', '', '', '', '', ''];
                                pasted.split('').forEach((char, index) => { next[index] = char; });
                                setDigits(next);
                            }}
                            rememberDevice={rememberDevice}
                            onRememberDeviceChange={setRememberDevice}
                            showRememberDevice
                            onSubmit={handleVerify}
                            onResend={handleResend}
                            error={error}
                            success={success}
                            resendBusy={resendBusy}
                            onBack={resetToLogin}
                        />
                    )}

                    {!profileLoading && !forceOnboarding && mode === 'forgot' && (
                        <ForgotPasswordForm
                            email={email}
                            onEmailChange={setEmail}
                            onSubmit={handleForgotPassword}
                            error={error}
                            success={success}
                            onBack={resetToLogin}
                        />
                    )}

                    {!profileLoading && !forceOnboarding && mode === 'reset-password' && (
                        resetVerifyLoading ? (
                            <div className="auth-loading">
                                <div className="loader"></div>
                                <p>Verifying reset link...</p>
                            </div>
                        ) : resetVerifyError ? (
                            <>
                                <h1>Reset link unavailable</h1>
                                <div className="auth-error"><i className="fa-solid fa-triangle-exclamation"></i>{resetVerifyError}</div>
                                <button type="button" className="btn-secondary auth-submit" onClick={resetToLogin}>Back to sign in</button>
                            </>
                        ) : (
                        <ResetPasswordForm
                            password={resetPassword}
                            confirmPassword={resetPasswordConfirm}
                            onPasswordChange={setResetPassword}
                            onConfirmPasswordChange={setResetPasswordConfirm}
                            onSubmit={handleResetPassword}
                            error={error}
                            onBack={resetToLogin}
                        />
                        )
                    )}

                    {!profileLoading && (forceOnboarding || mode === 'onboarding') && (
                        <OnboardingForm
                            step={onboardingStep}
                            jobRole={jobRole}
                            countryCode={countryCode}
                            usagePurpose={usagePurpose}
                            onSelectRole={setJobRole}
                            onCountryChange={setCountryCode}
                            onSelectPurpose={setUsagePurpose}
                            onContinue={handleOnboardingContinue}
                            error={error}
                        />
                    )}

                    {!profileLoading && !forceOnboarding && mode === 'accept-invitation' && (
                        <AcceptInvitationForm
                            loading={inviteLoading}
                            verifyError={inviteVerifyError}
                            details={inviteDetails}
                            email={inviteEmail}
                            existingPassword={inviteForm.existingPassword}
                            name={inviteForm.name}
                            title={inviteForm.title}
                            mobile={inviteForm.mobile}
                            defaultCountry={countryCode}
                            password={inviteForm.password}
                            error={error}
                            onChange={(field, value) => setInviteForm(prev => ({ ...prev, [field]: value }))}
                            onAcceptExisting={handleAcceptExisting}
                            onRegister={handleInviteRegister}
                            onBack={resetToLogin}
                        />
                    )}
                </div>
                <p className="auth-cookie-notice">
                    We store cookies and similar information on your device. This is necessary to use RuleStore Studio.{' '}
                    <a href="/cookies" target="_blank" rel="noopener noreferrer">Cookie Policy</a>
                </p>
            </div>
            <div className="auth-hero">
                <h2 className="auth-hero-tagline">RULE YOUR WORLD.</h2>
            </div>
        </div>
    );
}
